import React from "react";
import { motion, useAnimation } from "framer-motion";
import Plot from "react-plotly.js";
import { mergeMapboxConfig } from "@/lib/plotly-mapbox-config";
import { scatterPlotData, forestPlotData, violinPlotData, ridgePlotData } from "./multipanel-plot-data";

export function MultiPanelWindow({
  width = 700,
  height = 457,
  centered = false,
  style,
}: {
  width?: number;
  height?: number;
  centered?: boolean;
  style?: React.CSSProperties;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const libraryRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLDivElement>(null);
  const plotItemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const panelRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  
  const cursorControls = useAnimation();
  const dragThumbnailControls = useAnimation();
  const [showCursor, setShowCursor] = React.useState(false);
  const [showDragThumbnail, setShowDragThumbnail] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [hasDropped, setHasDropped] = React.useState(false);
  const [highlightDropZone, setHighlightDropZone] = React.useState(false);

  // Original design dimensions
  const originalWidth = 980;
  const originalHeight = 640;
  const scaleFactor = width / originalWidth;

  const wrapperStyle: React.CSSProperties = {
    width,
    height,
    transformOrigin: centered ? 'center center' : 'top left',
    overflow: 'hidden',
    ...style,
  };

  const innerStyle: React.CSSProperties = {
    width: originalWidth,
    height: originalHeight,
    transform: `scale(${scaleFactor})`,
    transformOrigin: 'top left',
  };

  if (centered) {
    wrapperStyle.position = 'absolute';
    wrapperStyle.left = '50%';
    wrapperStyle.top = '50%';
    wrapperStyle.transform = 'translate(-50%, -50%)';
    innerStyle.transformOrigin = 'center center';
    innerStyle.transform = `translate(${(originalWidth - width) / 2}px, ${(originalHeight - height) / 2}px) scale(${scaleFactor})`;
  }

  // Scroll-triggered animation start
  React.useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isAnimating && !hasDropped) {
            setTimeout(() => {
              setIsAnimating(true);
            }, 1200);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isAnimating, hasDropped]);

  // Animation sequence
  React.useEffect(() => {
    if (!isAnimating) return;
    let cancelled = false;
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      const container = containerRef.current;
      const sourceItem = plotItemRefs.current[3]; // Forest plot (4th item, index 3)
      const targetPanel = panelRefs.current[3];   // Panel (d)

      if (!container || !sourceItem || !targetPanel) {
        setIsAnimating(false);
        return;
      }

      // Wait a bit for layout to stabilize
      await sleep(100);

      const crect = container.getBoundingClientRect();
      const srect = sourceItem.getBoundingClientRect();
      const trect = targetPanel.getBoundingClientRect();

      // 1. Compute positions first
      const cursorAtSource = {
        left: (srect.left - crect.left + srect.width * 0.75 - 18) / scaleFactor,
        top: (srect.top - crect.top + srect.height * 0.5 - 13) / scaleFactor,
        opacity: 0,  // Start invisible
        scale: 1,
      };

      const thumbnailAtSource = {
        left: (srect.left - crect.left + srect.width * 0.5) / scaleFactor,
        top: (srect.top - crect.top + srect.height * 0.5) / scaleFactor,
        opacity: 0,  // Start invisible
        scale: 1,
      };

      // 2. Mount both elements (they will render with initial opacity 0)
      setShowDragThumbnail(true);
      setShowCursor(true);
      setHasDropped(false);
      setHighlightDropZone(false);

      // Give React a frame to mount the elements
      await sleep(16);

      // 3. Position both elements at correct location with opacity 0
      await cursorControls.set(cursorAtSource);
      await dragThumbnailControls.set(thumbnailAtSource);
      
      // 4. Fade both in together (include all properties to ensure animation works)
      await Promise.all([
        cursorControls.start({ 
          ...cursorAtSource, 
          opacity: 1, 
          transition: { duration: 0.1 } 
        }),
        dragThumbnailControls.start({ 
          ...thumbnailAtSource, 
          opacity: 1, 
          transition: { duration: 0.1 } 
        }),
      ]);
      
      await sleep(200);

      // 2. Click effect (pick up)
      await cursorControls.start({ scale: 0.92, transition: { duration: 0.1 } });
      await cursorControls.start({ scale: 1, transition: { duration: 0.1 } });
      await dragThumbnailControls.start({ scale: 0.95, transition: { duration: 0.1 } });
      await dragThumbnailControls.start({ scale: 1, transition: { duration: 0.1 } });
      await sleep(100);

      // 3. Animate along curve to panel (d)
      const cursorAtTarget = {
        left: (trect.left - crect.left + trect.width * 0.5 - 18) / scaleFactor,
        top: (trect.top - crect.top + trect.height * 0.5 - 13) / scaleFactor,
      };

      const thumbnailAtTarget = {
        left: (trect.left - crect.left + trect.width * 0.5) / scaleFactor,
        top: (trect.top - crect.top + trect.height * 0.5) / scaleFactor,
      };

      setHighlightDropZone(true);
      
      // Animate cursor and thumbnail together
      await Promise.all([
        cursorControls.start({
          ...cursorAtTarget,
          transition: { type: "tween", ease: "easeInOut", duration: 1.0 },
        }),
        dragThumbnailControls.start({
          ...thumbnailAtTarget,
          transition: { type: "tween", ease: "easeInOut", duration: 1.0 },
        }),
      ]);

      if (cancelled) return;

      // 4. Drop effect
      await Promise.all([
        cursorControls.start({ scale: 0.9, transition: { duration: 0.08 } }),
        dragThumbnailControls.start({ scale: 0.9, transition: { duration: 0.08 } }),
      ]);
      await Promise.all([
        cursorControls.start({ scale: 1, transition: { duration: 0.08 } }),
        dragThumbnailControls.start({ scale: 1, transition: { duration: 0.08 } }),
      ]);

      // 5. Fill panel (d) and fade out drag thumbnail
      setHasDropped(true);
      setHighlightDropZone(false);
      
      await dragThumbnailControls.start({
        opacity: 0,
        scale: 0.8,
        transition: { duration: 0.3 },
      });
      setShowDragThumbnail(false);
      
      await sleep(500);

      // 6. Cursor exits and fades
      await cursorControls.start({
        left: cursorAtTarget.left + 80,
        top: cursorAtTarget.top + 40,
        opacity: 0,
        transition: { duration: 0.5 },
      });

      setShowCursor(false);

      // Reset after pause
      await sleep(1600);
      if (!cancelled) {
        setIsAnimating(false);
        setHasDropped(false); // Reset so animation can replay
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isAnimating, cursorControls, dragThumbnailControls, scaleFactor]);

  // Plot data for thumbnails - memoized to prevent regeneration
  const plotThumbnails = React.useMemo(() => [
    {
      title: "Biomarker vs Week by Treatment",
      data: scatterPlotData,
    },
    {
      title: "Biomarker Distributions by Week...",
      data: ridgePlotData,
    },
    {
      title: "Biomarker at Week 12",
      data: violinPlotData,
    },
    {
      title: "Log Odds Ratios for Responder Rates...",
      data: forestPlotData,
    },
  ], []);

  return (
    <div style={wrapperStyle}>
      <div style={innerStyle}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative select-none overflow-hidden rounded-[10px] border border-black/10 bg-white/95 shadow-[0_28px_70px_rgba(0,0,0,0.14),_0_14px_32px_rgba(0,0,0,0.10),_0_0_0_1px_rgba(0,0,0,0.08)]"
          style={{ width: originalWidth, height: originalHeight }}
        >
          {/* Window bar */}
          <div className="relative flex h-7 items-center justify-between border-b border-black/10 px-2 bg-neutral-50/90">
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300" />
            </div>
            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs text-neutral-700">
              Multipanel figure builder
            </div>
          </div>

          <div
            ref={containerRef}
            className="flex h-[calc(100%-28px)] w-full relative"
          >
            {/* Left sidebar - Plot library */}
            <div
              ref={libraryRef}
              className="w-[200px] border-r border-neutral-200 bg-neutral-50 flex flex-col flex-shrink-0"
            >
              {/* Header */}
              <div className="px-3 pt-3 pb-2 border-b border-neutral-200">
                <div className="text-xs font-semibold text-neutral-700 mb-2">Plots</div>
                <input
                  type="text"
                  placeholder="Search plots..."
                  className="w-full h-7 px-2 text-xs border border-neutral-300 rounded bg-white"
                  disabled
                />
              </div>

              {/* Plot list */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                {plotThumbnails.map((plot, idx) => (
                  <PlotThumbnail
                    key={idx}
                    ref={(el) => { plotItemRefs.current[idx] = el; }}
                    plot={plot}
                  />
                ))}
              </div>
            </div>

            {/* Right canvas */}
            <div
              ref={canvasRef}
              className="flex-1 bg-slate-50/80 overflow-auto flex flex-col"
            >
              {/* Canvas header */}
              <div className="px-6 py-3 border-b border-neutral-200 bg-white/90 flex items-center justify-between flex-shrink-0">
                <div className="text-sm font-semibold text-neutral-800">
                  Figure 1. Biomarker trial overview
                </div>
                <div className="px-2 py-0.5 bg-neutral-100 rounded text-[10px] text-neutral-600 font-medium">
                  Multipanel
                </div>
              </div>

              {/* Panel grid */}
              <div className="p-6 flex-1 min-h-0">
                <div className="relative bg-white border border-neutral-200 rounded-lg p-4 h-full flex flex-col">
                  <div className="grid grid-cols-[1fr_1fr] grid-rows-[1fr_1fr_1fr] gap-3 flex-1 min-h-0">
                    {/* Panel (a) - full height on left */}
                    <div className="row-span-3 min-h-0">
                      <Panel
                        ref={(el) => { panelRefs.current[0] = el; }}
                        label="(a)"
                        title="Biomarker vs Week by Treatment"
                        plotData={plotThumbnails[0].data}
                      />
                    </div>

                    {/* Panel (b) - top right */}
                    <div className="min-h-0">
                      <Panel
                        ref={(el) => { panelRefs.current[1] = el; }}
                        label="(b)"
                        title="Biomarker Distributions by Week and Treatment"
                        plotData={plotThumbnails[1].data}
                      />
                    </div>

                    {/* Panel (c) - middle right */}
                    <div className="min-h-0">
                      <Panel
                        ref={(el) => { panelRefs.current[2] = el; }}
                        label="(c)"
                        title="Biomarker by Treatment at Week 12"
                        plotData={plotThumbnails[2].data}
                      />
                    </div>

                    {/* Panel (d) - bottom right, empty initially, filled on drop */}
                    <div className="min-h-0">
                      <Panel
                        ref={(el) => { panelRefs.current[3] = el; }}
                        label="(d)"
                        title="Log Odds Ratios for Responder Rates by Site at Week 12"
                        plotData={hasDropped ? plotThumbnails[3].data : null}
                        highlight={highlightDropZone}
                        isEmpty={!hasDropped}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Animated cursor */}
            {showCursor && (
              <motion.div
                className="absolute pointer-events-none z-50"
                initial={{ opacity: 0 }}
                animate={cursorControls}
                style={{ transformOrigin: "18px 13px" }}
              >
                <svg width="22" height="26" viewBox="0 0 24 28" fill="none" className="drop-shadow-md" aria-hidden="true">
                  <path
                    d="M3 2 L3 22 L9 16 L12.5 24 L14.7 23 L11.3 15 L19.5 15 Z"
                    fill="#111111"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>
            )}

            {/* Drag thumbnail */}
            {showDragThumbnail && (
              <motion.div
                className="absolute pointer-events-none z-40"
                initial={{ opacity: 0 }}
                animate={dragThumbnailControls}
                style={{ transformOrigin: "center center" }}
              >
                <div className="w-20 h-12 bg-white border-2 border-blue-500 rounded shadow-lg overflow-hidden">
                  <Plot
                    data={plotThumbnails[3].data.data as any}
                    layout={{
                      ...plotThumbnails[3].data.layout,
                      width: 80, // w-20 = 80px
                      height: 48, // h-12 = 48px
                      autosize: false,
                      margin: { l: 2, r: 2, t: 2, b: 2 },
                      font: { size: 5 },
                      showlegend: false,
                      xaxis: {
                        ...plotThumbnails[3].data.layout.xaxis,
                        showticklabels: false,
                        showgrid: true,
                        gridcolor: "rgba(0,0,0,0.05)",
                        title: { text: "" },
                        automargin: false,
                        domain: [0, 1],
                      },
                      yaxis: {
                        ...plotThumbnails[3].data.layout.yaxis,
                        showticklabels: false,
                        showgrid: true,
                        gridcolor: "rgba(0,0,0,0.05)",
                        title: { text: "" },
                        automargin: false,
                        domain: [0, 1],
                      },
                    } as any}
                    config={{ displayModeBar: false, responsive: false, staticPlot: true }}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Panel component
const Panel = React.forwardRef<
  HTMLDivElement,
  {
    label: string;
    title: string;
    plotData: any;
    highlight?: boolean;
    isEmpty?: boolean;
  }
>(({ label, title, plotData, highlight, isEmpty }, ref) => {
  const plotContainerRef = React.useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = React.useState<{ width: number; height: number } | null>(null);

  // Measure container size for Plotly
  // Use offsetWidth/offsetHeight instead of getBoundingClientRect() to avoid
  // double-scaling issues when parent has CSS transform: scale()
  React.useEffect(() => {
    if (!plotContainerRef.current || isEmpty) return;
    
    const updateSize = () => {
      const el = plotContainerRef.current;
      if (el) {
        setContainerSize({ width: el.offsetWidth, height: el.offsetHeight });
      }
    };
    
    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(plotContainerRef.current);
    
    return () => resizeObserver.disconnect();
  }, [isEmpty]);
  
  // Strip fixed ranges to allow Plotly to autorange based on actual data
  const getYAxisConfig = () => {
    if (!plotData?.layout?.yaxis) return {};
    
    const originalYAxis = plotData.layout.yaxis;
    
    // For categorical y-axis (like forest plot), keep as-is but ensure proper scaling
    if (originalYAxis.type === "category") {
      const { automargin: _am, ...rest } = originalYAxis;
      return {
        ...rest,
        titlefont: { size: 10 },
        tickfont: { size: 9 },
        automargin: false, // CRITICAL: Disable automargin
        domain: [0, 1], // Use full vertical space
      };
    }
    
    // For numeric y-axis, remove fixed range to allow autorange (shows all data)
    // Preserve autorange: "reversed" if it was set, otherwise use true
    const { range, autorange, automargin: _am, ...rest } = originalYAxis;
    
    // Preserve "reversed" if it was set, otherwise default to true for autorange
    const finalAutorange = autorange === "reversed" ? "reversed" : (autorange ?? true);
    
    return {
      ...rest,
      autorange: finalAutorange, // Enable autorange to show all data without clipping
      titlefont: { size: 10 },
      tickfont: { size: 9 },
      automargin: false, // CRITICAL: Disable automargin
      domain: [0, 1], // Fill full height
    };
  };

  return (
    <div
      ref={ref}
      className={`relative border rounded px-2 pt-2 pb-1 bg-white pointer-events-none h-full flex flex-col min-h-0 ${
        highlight
          ? "border-blue-500 shadow-lg shadow-blue-200/50"
          : isEmpty
          ? "border-dashed border-neutral-300"
          : "border-neutral-200"
      } transition-all`}
    >
      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center pt-4 min-h-0">
          <div className="text-center text-[10px] text-neutral-400">
            Click to assign a plot or drag from library
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col pt-1 min-h-0 overflow-hidden">
          <div className="text-[10px] text-neutral-600 mb-1 line-clamp-1 flex-shrink-0">
            <span className="font-bold">{label} {title}</span>
          </div>
          <div 
            ref={plotContainerRef}
            className="flex-1 min-h-0 w-full pointer-events-none"
          >
            {plotData && containerSize && (() => {
              // Remove width/height/autosize from original layout to prevent aspect ratio constraints
              const { width: _w, height: _h, autosize: _a, margin: _m, ...baseLayout } = plotData.layout;
              
              // Get original layout dimensions for scaling calculations
              const originalWidth = (plotData.layout as any).width ?? 600;
              const originalHeight = (plotData.layout as any).height ?? 400;
              
              // Calculate scale factors to adjust margins proportionally
              const scaleX = containerSize.width / originalWidth;
              const scaleY = containerSize.height / originalHeight;
              
              // Scale margins from original layout, with minimums to prevent clipping
              const originalMargin = plotData.layout.margin ?? { l: 50, r: 20, t: 10, b: 40 };
              const marginL = Math.max(24, Math.round((originalMargin.l ?? 50) * scaleX));
              const marginR = Math.max(6, Math.round((originalMargin.r ?? 20) * scaleX));
              const marginT = Math.max(6, Math.round((originalMargin.t ?? 10) * scaleY));
              const marginB = Math.max(18, Math.round((originalMargin.b ?? 40) * scaleY));
              
              return (
                <Plot
                  data={plotData.data as any}
                  layout={{
                    ...baseLayout,
                    width: containerSize.width,
                    height: containerSize.height,
                    autosize: false, // CRITICAL: Disable autosize to fill container exactly
                    margin: {
                      l: marginL,
                      r: marginR,
                      t: marginT,
                      b: marginB,
                      autoexpand: false,
                      pad: 0,
                    },
                    font: { size: 10 },
                    xaxis: (() => {
                      const originalXAxis = plotData.layout.xaxis ?? {};
                      const { range, autorange, automargin, ...rest } = originalXAxis;
                      return {
                        ...rest,
                        autorange: autorange ?? true, // Keep autorange to show all data
                        automargin: false, // CRITICAL: Disable automargin
                        titlefont: { size: 10 },
                        tickfont: { size: 9 },
                        domain: [0, 1], // Fill full width
                      };
                    })(),
                    yaxis: (() => {
                      const config = getYAxisConfig();
                      return {
                        ...config,
                        automargin: false, // CRITICAL: Disable automargin
                        domain: [0, 1], // Fill full height
                      };
                    })(),
                  } as any}
                  config={mergeMapboxConfig({
                    displayModeBar: false,
                    staticPlot: true,
                    responsive: false, // CRITICAL: Disable responsive to use exact dimensions
                  })}
                  style={{ width: "100%", height: "100%" }}
                />
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
});

Panel.displayName = "Panel";

// Thumbnail component for sidebar plots
const PlotThumbnail = React.forwardRef<
  HTMLDivElement,
  {
    plot: { title: string; data: any };
  }
>(({ plot }, ref) => {
  const thumbnailRef = React.useRef<HTMLDivElement>(null);
  const [thumbnailSize, setThumbnailSize] = React.useState<{ width: number; height: number } | null>(null);

  // Measure thumbnail container size using offsetWidth/offsetHeight
  React.useEffect(() => {
    const updateSize = () => {
      const el = thumbnailRef.current;
      if (el) {
        setThumbnailSize({ width: el.offsetWidth, height: el.offsetHeight });
      }
    };
    
    updateSize();
    const resizeObserver = new ResizeObserver(updateSize);
    if (thumbnailRef.current) {
      resizeObserver.observe(thumbnailRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="p-1.5 bg-white border border-neutral-200 rounded cursor-pointer hover:shadow-sm transition-shadow"
    >
      <div className="text-[10px] font-medium text-neutral-700 mb-0.5 line-clamp-2">
        {plot.title}
      </div>
      <div className="text-[9px] text-neutral-500 mb-1">Updated Nov 19</div>
      <div 
        ref={thumbnailRef}
        className="h-[60px] w-full bg-white rounded overflow-hidden pointer-events-none"
      >
        {thumbnailSize && (
          <Plot
            data={plot.data.data as any}
            layout={{
              ...plot.data.layout,
              width: thumbnailSize.width,
              height: thumbnailSize.height,
              autosize: false,
              margin: { l: 6, r: 3, t: 3, b: 6 },
              font: { size: 5 },
              showlegend: false,
              xaxis: {
                ...plot.data.layout.xaxis,
                showticklabels: false,
                showgrid: true,
                gridcolor: "rgba(0,0,0,0.05)",
                title: { text: "" },
                automargin: false,
                domain: [0, 1],
              },
              yaxis: {
                ...plot.data.layout.yaxis,
                showticklabels: false,
                showgrid: true,
                gridcolor: "rgba(0,0,0,0.05)",
                title: { text: "" },
                automargin: false,
                domain: [0, 1],
              },
            } as any}
            config={{ displayModeBar: false, responsive: false, staticPlot: true }}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </div>
    </div>
  );
});

PlotThumbnail.displayName = "PlotThumbnail";

